import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import logoAlbaker from "@/assets/logo-albaker.png";
import { Save, Lock, LogOut } from "lucide-react";

const Account = () => {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingName, setSavingName] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/login");
    if (user) setName(user.name || "");
  }, [user, navigate]);

  const updateName = async (e) => {
    e.preventDefault();
    setSavingName(true);
    try {
      await api.patch("/auth/me", { name: name.trim() });
      await refresh();
      toast.success("تم تحديث اسمك");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingName(false);
    }
  };

  const updatePwd = async (e) => {
    e.preventDefault();
    if (pwd.new_password.length < 8) return toast.error("كلمة المرور يجب أن تكون ٨ أحرف على الأقل");
    if (pwd.new_password !== pwd.confirm) return toast.error("كلمتا المرور غير متطابقتين");
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      setPwd({ current_password: "", new_password: "", confirm: "" });
      toast.success("تم تغيير كلمة المرور");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPwd(false);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  if (!user) return null;

  return (
    <div dir="rtl" className="min-h-screen bg-ink text-bone">
      <Toaster position="top-left" theme="dark" richColors />
      <div className="container-x py-20 max-w-3xl">
        <div className="flex items-center justify-between mb-14">
          <Link to="/" className="inline-flex items-center gap-3">
            <img src={logoAlbaker} alt="" className="h-10 w-auto" />
            <span className="overline">العودة للموقع</span>
          </Link>
          <button
            type="button"
            onClick={onLogout}
            data-testid="account-logout"
            className="inline-flex items-center gap-2 text-white/60 hover:text-red-400 transition-colors text-sm"
          >
            <LogOut size={14} />
            <span className="font-arabic">تسجيل الخروج</span>
          </button>
        </div>

        <div className="overline mb-3">ملفي الشخصي</div>
        <h1 className="font-arabic font-black text-4xl sm:text-5xl text-bone mb-10">
          {user.name}
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-12">
          <Stat label="البريد الإلكتروني" value={user.email} ltr />
          <Stat label="الدور" value={user.role === "admin" ? "مدير" : "عضو"} />
          <Stat label="انضم في" value={(user.created_at || "").slice(0, 10)} ltr />
        </div>

        {/* Update name */}
        <form onSubmit={updateName} className="border border-white/10 bg-ink-soft p-8 mb-10 space-y-5" data-testid="account-update-name-form">
          <div className="overline">المعلومات الأساسية</div>
          <label className="block">
            <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50">الاسم</span>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="editorial-input"
              data-testid="account-name"
            />
          </label>
          <button type="submit" disabled={savingName} className="btn-gold disabled:opacity-60" data-testid="account-name-save">
            <Save size={14} />
            <span>{savingName ? "جاري الحفظ…" : "حفظ"}</span>
          </button>
        </form>

        {/* Change password */}
        <form onSubmit={updatePwd} className="border border-white/10 bg-ink-soft p-8 space-y-5" data-testid="account-password-form">
          <div className="overline flex items-center gap-3"><Lock size={14} /> الأمان</div>
          <label className="block">
            <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50">كلمة المرور الحالية</span>
            <input type="password" required value={pwd.current_password} onChange={(e) => setPwd((p) => ({ ...p, current_password: e.target.value }))} className="editorial-input" dir="ltr" data-testid="account-current-pwd" />
          </label>
          <label className="block">
            <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50">كلمة المرور الجديدة</span>
            <input type="password" required value={pwd.new_password} onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))} className="editorial-input" dir="ltr" data-testid="account-new-pwd" />
          </label>
          <label className="block">
            <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50">تأكيد كلمة المرور الجديدة</span>
            <input type="password" required value={pwd.confirm} onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))} className="editorial-input" dir="ltr" data-testid="account-confirm-pwd" />
          </label>
          <button type="submit" disabled={savingPwd} className="btn-gold disabled:opacity-60" data-testid="account-password-save">
            <Save size={14} />
            <span>{savingPwd ? "جاري الحفظ…" : "تحديث كلمة المرور"}</span>
          </button>
        </form>
      </div>
    </div>
  );
};

const Stat = ({ label, value, ltr }) => (
  <div className="bg-ink p-6">
    <div className="overline mb-3">{label}</div>
    <div className={`text-bone text-base ${ltr ? "" : "font-arabic"}`} dir={ltr ? "ltr" : "auto"}>{value || "—"}</div>
  </div>
);

export default Account;
